// ──── WebSocket Protocol (mirrors backend skywatch-hub-agent-svc/src/types/agent.ts) ────

export type StreamEventType =
  | 'conversation_created'
  | 'token'
  | 'tool_call'
  | 'tool_result'
  | 'done'
  | 'error'
  | 'usage'
  | 'cancelled';

export interface WebSocketEvent {
  type: StreamEventType;
  data: unknown;
  timestamp: number;
}

export interface ConversationCreatedEvent {
  conversationId: string;
  title: string;
}

export interface TokenEvent {
  text: string;
}

export interface ToolCallEvent {
  toolUseId: string;
  toolName: string;
  input: Record<string, unknown>;
}

export interface ToolResultEvent {
  toolUseId: string;
  toolName: string;
  status: 'success' | 'error';
  result: unknown;
}

export interface DoneEvent {
  conversationId: string;
  messageId?: string;
}

export interface ErrorEvent {
  code: string;
  message: string;
}

export interface UsageEvent {
  inputTokens: number;
  outputTokens: number;
}

// ──── WebSocket Send Actions ────

export interface WebSocketSendMessage {
  action: 'chat';
  message: string;
  conversationId?: string;
  context?: PageContext;
}

export interface WebSocketCancelMessage {
  action: 'cancel';
  conversationId: string;
}

// ──── Page Context ────

export interface CartContextItem {
  id: string;
  source: string;
  date: string;
  price: number;
  resolution: string;
  type: string;
}

export interface PageContext {
  page?: string;
  projectId?: string;
  searchId?: string;
  orderId?: string;
  cart?: {
    itemCount: number;
    totalPrice: number;
    items: CartContextItem[];
  };
}

// ──── Chat Messages (UI representation) ────

export type MessageRole = 'user' | 'assistant';

export interface ChatMessage {
  messageId: string;
  role: MessageRole;
  content: string;
  createdAt: string;
  tokenUsage?: {
    inputTokens: number;
    outputTokens: number;
  };
  feedback?: 'positive' | 'negative';
  toolCalls?: ToolCallDisplay[];
}

export interface ToolCallDisplay {
  toolUseId: string;
  toolName: string;
  input: Record<string, unknown>;
  status: 'pending' | 'success' | 'error';
  result?: unknown;
}

// ──── Conversation ────

export interface Conversation {
  conversationId: string;
  title: string;
  status: 'active' | 'archived';
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

// ──── Agent State ────

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface AgentState {
  panelOpen: boolean;
  connectionStatus: ConnectionStatus;
  activeConversationId: string | null;
  messages: ChatMessage[];
  conversations: Conversation[];
  isStreaming: boolean;
  streamingContent: string;
  streamingToolCalls: ToolCallDisplay[];
  error: string | null;
  showHistory: boolean;
}

// ──── Reducer Actions ────

export type AgentAction =
  | { type: 'TOGGLE_PANEL' }
  | { type: 'OPEN_PANEL' }
  | { type: 'CLOSE_PANEL' }
  | { type: 'SET_CONNECTION_STATUS'; payload: ConnectionStatus }
  | { type: 'SET_ACTIVE_CONVERSATION'; payload: string }
  | { type: 'CLEAR_CONVERSATION' }
  | { type: 'ADD_USER_MESSAGE'; payload: ChatMessage }
  | { type: 'SET_MESSAGES'; payload: ChatMessage[] }
  | { type: 'SET_CONVERSATIONS'; payload: Conversation[] }
  | { type: 'START_STREAMING' }
  | { type: 'APPEND_TOKEN'; payload: string }
  | { type: 'ADD_TOOL_CALL'; payload: ToolCallDisplay }
  | {
      type: 'UPDATE_TOOL_CALL';
      payload: { toolUseId: string; status: 'success' | 'error'; result?: unknown };
    }
  | {
      type: 'FINISH_STREAMING';
      payload: {
        messageId?: string;
        tokenUsage?: { inputTokens: number; outputTokens: number };
      };
    }
  | {
      type: 'SET_MESSAGE_FEEDBACK';
      payload: { messageId: string; feedback?: 'positive' | 'negative' };
    }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'TOGGLE_HISTORY' };

// ──── REST API Response Types ────

export interface ListConversationsResponse {
  conversations: Conversation[];
  count: number;
}

export interface GetConversationResponse {
  conversation: Conversation;
  messages: ChatMessage[];
}

export interface FeedbackResponse {
  status: string;
  feedbackId: string;
}

// ──── Tool Display Labels ────

export const TOOL_LABELS: Record<string, string> = {
  catalog_search: 'Searching imagery archive',
  pricing_calculator: 'Calculating pricing',
  data_product_explainer: 'Looking up product details',
  project_status: 'Checking project status',
  offerings_browser: 'Browsing available products',
  user_context: 'Checking account details',
  esri_publisher: 'Publishing to ArcGIS',
  search_refiner: 'Refining search results',
  order_filter: 'Filtering orders',
  cart_manager: 'Managing cart',
  data_delivery: 'Checking delivery status',
  billing_manager: 'Checking billing info',
  tasking_planner: 'Checking tasking availability',
  page_navigator: 'Navigating to page',
};
